<template>
  <BorrowerAttributeConstructor
    :questionText="questionText"
    :description="description"
    :descriptionTooltip="descriptionTooltip"
    :tooltipText="tooltipText"
    :helpText="helpText"
    :disclaimer="disclaimer"
    :errorText="errorText"
  >
    <template v-slot:input>
      <div role="radiogroup" :aria-labelledby="questionUniqueId+'-label'" class="p-0">
        <label :id="questionUniqueId+'-label'" class="mb-3">{{ label }}</label>
        <div :dreams-id="question.alias">
          <fieldset class="box-select-wrapper"
            :class="{
                'grid-col-2': question.metadata && question.metadata.columns == 2
              }"
            >
            <button
              v-for="(option) in question.options"
              :data-cy="`box-select-${question.alias}-${option.value}`"
              type="button"
              role="radio"
              class="selection-pill text-center"
              :class="{
                'selection-pill--selected': matchesOption(option),
                'selection-pill--invalid': v$.value && v$.value.$error,
              }"
              :aria-checked="option.value === value ? 'true' : 'false'"
              @click="selectOption(option)"
              @keydown.space="selectOption(option, $event)"
              @blur="onBlur()"
              :key="option.value">
              {{ option.label }}
            </button>
          </fieldset>
        </div>
      </div>
    </template>
  </BorrowerAttributeConstructor>
</template>

<script>
import { mapState } from 'pinia'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'
import BaseBorrowerAttributeMixin from './BaseBorrowerAttributeMixin'
import { useQuestionsStore } from '~/store/questions'

export default {
  setup: () => ({ v$: useVuelidate() }),
  props: {
    v: Object
  },
  emits: ['invalidity','answer'],
  created: function() {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})
  },
  computed: {
    ...mapState(useQuestionsStore, ['singleForm', 'validateSingleForm']),
    invalid() {
      return this.v$.$invalid
    },
    errorText() {
      let errorMsg = ''
      if (this.v$.value?.$error && this.question.alias === 'borrower.stateId') {
        errorMsg = 'Please select a state'
      } else if (this.v$.value?.$error && this.question.alias === 'businessType') {
        errorMsg = 'Please select a business type'
      } else if (this.v$.value?.$error) {
        errorMsg = 'Please select a value'
      }
      return errorMsg
    },
    noneFocused() {
      return this.question.options.filter(o => o.value === this.value).length === 0
    },
    matchesOption() {
      return (option) => {
        if (option.range && this.value) {
          let {min, max} = option.range
          let value
          min = +min;
          max = +max;
          try {
            value = parseInt(this.value)
          } catch (e) {
            return false
          }
          return option.value === this.value || (!min || value >= +min) && (!max || value <= max)
        }
        return option.value === this.value
      }
    },
  },
  mixins: [BaseBorrowerAttributeMixin],
  validations() {
    return {
      value: { required: requiredIf(() => !this.question.mustAllowNull) }
    }
  },
  methods: {
    selectOption (option, event) {
      if (event) {
        event.stopPropagation()
      }
      this.value = option.value
      this.v$.$touch()
      this.$emit('answer', { value: option.value, isInvalid: this.v$.value.$error, submitImmediately: true })
    },
    onBlur () {
      this.v$.$touch()
    }
  },
}
</script>

<style scoped lang="scss">
.selection-pill {
  min-height: 48px;
  color: $type-400;
  line-height: 18px;
  font-size: 14px;

  border-radius: 4px;
  border: 1px solid $border-100;
  padding: 7px;

  cursor: pointer;
  background-color: $white;
}

.selection-pill:target, .selection-pill:focus, .selection-pill:hover {
  box-shadow: 0 0 0.25rem 0.125rem $neutral-100;
}
.selection-pill--selected {
  border: 2px solid $blue-300;
  padding: 6px;
}
.selection-pill--invalid {
  border-color: $red;
}

.box-select-wrapper {
  display: grid;
  grid-template-columns: repeat(3, calc((100% - 1em) / 3));
  grid-gap: 0.5em;

  &.grid-col-2 {
    grid-template-columns: repeat(2, calc((100% - 0.5em) / 2));
  }
}
</style>
